import { ReactNode, useState } from "react";
import { ButtonBar, Input } from "@k8slens/lds";

import clsx from "clsx";

import { personalEligibilityText } from "src/services/plans";

import Button from "src/components/Button/TrackedButton";

import styles from "./Eligibility.module.css";

export type EligibilityState = "unset" | "error" | "pending-user-action" | "pending-response" | "ineligible" | "eligible";

interface Props {
  className?: string;
  buttonLabel: string | ReactNode;
  loading: boolean;
  current: boolean;
  disabled: boolean;
  discreet: boolean;
  state: EligibilityState;
  onChange: (state: EligibilityState) => Promise<void>;
}

export const Eligibility = ({
  className, disabled, discreet, current, loading, buttonLabel, state, onChange
}: Props) => {
  const [inputValue, setInputValue] = useState("");

  if (state === "unset" || state === "error") {
    return (
      <ButtonBar type="stacked" className={className}>
        <>
          <div />
          <Button
            id={current ? "current-plan" : ""}
            loading={loading}
            label={buttonLabel}
            disabled={disabled || current}
            discreet={discreet}
            primary
            onClick={() => onChange("pending-user-action")}
            aria-label="Choose Personal Plan"
            allCaps
          />
        </>
      </ButtonBar>
    );
  }

  if (state === "pending-user-action" || state === "pending-response") {
    const confirmEnabled = inputValue === "CONFIRM";

    return (
      <footer role="dialog" className={clsx(className, styles.queryEligibility)}>
        <p>
          Type
          {" "}
          <code>CONFIRM</code>
          {" "}
          in the field below to confirm you are
          {" "}
          <dfn title={personalEligibilityText}>eligible</dfn>
          ; you or your company have less than $10M in annual revenue or funding.
        </p>
        <form onSubmit={(e) => {
          e.preventDefault();
          onChange("pending-response");
          setInputValue("");
        }}
        >
          <ButtonBar type="grid">
            <Input
              value={inputValue}
              aria-label="Type CONFIRM to continue"
              onChange={(e) => setInputValue(e.currentTarget.value.toUpperCase())}
              autoFocus
            />
            <Button
              label="Ok"
              primary
              disabled={!confirmEnabled}
              loadingStateOnPromise
              aria-label="Click to confirm"
              buttonType="submit"
              allCaps
            />
          </ButtonBar>
        </form>
      </footer>
    );
  }

  if (state === "ineligible") {
    return (
      <footer className={clsx(className, styles.ineligible)}>
        <h5>You are not eligible for this plan</h5>
        {/* eslint-disable-next-line max-len */}
        <p>Your revenue or funding should be less than $10M in the last 12 months. You are eligible for the Pro plan.</p>
      </footer>
    );
  }

  return <footer />;
};
